import { BRANCHES, COMPANY } from "../lib/data";
import { Instagram, Facebook, Twitter, MapPin, Phone, Mail } from "lucide-react";
import { Link } from "react-router-dom";
import { useSettings } from "../lib/settings";
import { formatIndianPhone, telHref } from "../lib/phone";
import { trackEvent } from "../lib/analytics";

export const Footer = () => {
  const { logoUrl } = useSettings();
  return (
    <footer
      data-testid="main-footer"
      className="relative border-t border-black/5 bg-[#efefe9]"
    >
      <div className="absolute inset-0 bg-grid opacity-50" />
      <div className="relative mx-auto max-w-[1400px] px-6 py-20 lg:px-10">
        {/* Top */}
        <div className="grid gap-12 md:grid-cols-12">
          <div className="md:col-span-4">
            <div className="flex items-center gap-3">
              <img
                src={logoUrl}
                alt="Baheti Steel Traders"
                className="h-12 w-12 object-contain"
                data-testid="footer-logo-img"
              />
              <div>
                <div className="font-display text-xl font-extrabold uppercase tracking-tight text-[#1c1c1e] md:text-2xl">
                  Baheti Steel Traders LLP
                </div>
                <div className="mt-0.5 font-mono-industrial text-[10px] uppercase tracking-[0.3em] text-[#8b8b8f]">
                  Since {COMPANY.foundedYear}
                </div>
              </div>
            </div>
            <p className="mt-6 max-w-sm text-sm leading-relaxed text-[#4a4a4e]">
              A {COMPANY.legacyYears}-year legacy in TMT distribution, built on accurate
              weighbridges, fair pricing and deep relationships with the
              twin cities&rsquo; finest builders and homeowners.
            </p>
            <div className="mt-8 flex gap-3">
              <a
                href={COMPANY.socials.instagram}
                target="_blank"
                rel="noreferrer"
                data-testid="footer-instagram"
                className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-black/10 bg-white text-[#4a4a4e] transition-all duration-300 hover:border-[#e85a1c] hover:bg-[#e85a1c] hover:text-white"
              >
                <Instagram size={15} />
              </a>
              <a
                href={COMPANY.socials.facebook}
                target="_blank"
                rel="noreferrer"
                data-testid="footer-facebook"
                className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-black/10 bg-white text-[#4a4a4e] transition-all duration-300 hover:border-[#e85a1c] hover:bg-[#e85a1c] hover:text-white"
              >
                <Facebook size={15} />
              </a>
              <a
                href={COMPANY.socials.twitter}
                target="_blank"
                rel="noreferrer"
                data-testid="footer-twitter"
                className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-black/10 bg-white text-[#4a4a4e] transition-all duration-300 hover:border-[#e85a1c] hover:bg-[#e85a1c] hover:text-white"
              >
                <Twitter size={15} />
              </a>
            </div>
          </div>

          <div className="md:col-span-8">
            <div className="font-mono-industrial text-[10px] uppercase tracking-[0.3em] text-[#e85a1c]">
              Our Branches · Hyderabad &amp; Secunderabad
            </div>
            <div className="mt-6 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
              {BRANCHES.map((b, i) => (
                <div
                  key={b.name}
                  data-testid={`footer-branch-${i}`}
                  className="group rounded-xl border border-black/5 bg-white/70 p-5 backdrop-blur transition-all duration-300 hover:-translate-y-1 hover:border-[#e85a1c]/40 hover:bg-white shadow-lux"
                >
                  <div className="font-display-med text-[15px] uppercase leading-snug tracking-tight text-[#1c1c1e]">
                    {b.name}
                  </div>
                  <div className="mt-4 space-y-2.5 text-[12px] leading-relaxed text-[#4a4a4e]">
                    <div className="flex gap-2">
                      <MapPin size={13} className="mt-0.5 shrink-0 text-[#e85a1c]" />
                      <span>{b.address}</span>
                    </div>
                    <div className="flex gap-2">
                      <Phone size={13} className="mt-0.5 shrink-0 text-[#e85a1c]" />
                      <a
                        href={telHref(b.phone)}
                        onClick={() => trackEvent("phone_click", { source: "footer_branch", branch: b.name, phone_number: b.phone })}
                        className="font-mono-industrial hover:text-[#e85a1c]"
                      >
                        {formatIndianPhone(b.phone)}
                      </a>
                    </div>
                    <div className="flex gap-2">
                      <Mail size={13} className="mt-0.5 shrink-0 text-[#e85a1c]" />
                      <a
                        href={`mailto:${b.email}`}
                        className="break-all hover:text-[#e85a1c]"
                      >
                        {b.email}
                      </a>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-16 flex flex-col items-start justify-between gap-4 border-t border-black/10 pt-6 text-xs text-[#8b8b8f] md:flex-row md:items-center">
          <div className="flex flex-wrap gap-x-5 gap-y-2">
            <Link to="/" className="hover:text-[#e85a1c]" data-testid="footer-link-home">Home</Link>
            <Link to="/about/team" className="hover:text-[#e85a1c]" data-testid="footer-link-about">About</Link>
            <Link to="/products" className="hover:text-[#e85a1c]" data-testid="footer-link-products">Products &amp; Services</Link>
            <Link to="/price" className="hover:text-[#e85a1c]" data-testid="footer-link-price">Price</Link>
            <Link to="/blog" className="hover:text-[#e85a1c]" data-testid="footer-link-blog">Blog</Link>
          </div>
          <span className="font-mono-industrial uppercase tracking-[0.25em]">© {new Date().getFullYear()} Baheti Steel Traders · Hyderabad</span>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
